import { CreditCard, Search } from "lucide-react";
import { useTranslation } from "react-i18next";
import { IdentificationModeTabs } from "../../scheduler/identification/IdentificationModeTabs";

type PrescriptionIdentificationMode = "card" | "search";

/**
 * The prescription flow's card / name-search switch, rendered with the same
 * tabs as the scheduler identification (KO-07a). The two modes are machine
 * states here (`cardCheck`, `patientLookup`), so a tab sends the transition.
 * The search tab keeps the e2e id `frontdesk-prescription-card-manual-lookup-btn`.
 *
 * @since 1.3.0
 * @example
 * ```tsx
 * <PrescriptionIdentificationTabs activeMode="card" onCard={() => undefined} onSearch={() => send({ type: "MANUAL_LOOKUP" })} />
 * ```
 */
export function PrescriptionIdentificationTabs({
  activeMode,
  onCard,
  onSearch,
  searchDisabled = false,
}: {
  readonly activeMode: PrescriptionIdentificationMode;
  readonly onCard: () => void;
  readonly onSearch: () => void;
  /** While a card is being read, switching away is not offered. */
  readonly searchDisabled?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <IdentificationModeTabs<PrescriptionIdentificationMode>
      label={t("self-service.identification.mode-label")}
      activeMode={activeMode}
      modes={[
        {
          id: "card",
          icon: CreditCard,
          label: t("self-service.identification.mode-card"),
          testId: "frontdesk-prescription-identify-card-tab",
        },
        {
          id: "search",
          icon: Search,
          label: t("self-service.identification.mode-search"),
          testId: "frontdesk-prescription-card-manual-lookup-btn",
        },
      ]}
      onModeChange={(mode) => {
        if (mode === activeMode) return;
        if (mode === "search" && !searchDisabled) onSearch();
        if (mode === "card") onCard();
      }}
    />
  );
}
